/**
 * * @perfTrack 用户收集并上报网站性能数据
 */
import ReactGA from "react-ga";
// import commonStore from "../stores/commonStore";
class PerfTrack {
  constructor({ baseGaCategory, baseGa4Category }) {
    this.init(baseGaCategory, baseGa4Category);
  }

  // 是否满足初始化要求，校验API是否支持。
  ready = false;
  // 页面url是否改变，变化以后如果还没上报则停止上报。
  urlChanged = false;
  // setInterval的ID
  intervalTag = undefined;
  // 文件匹配正则。
  regOfJs = /\.js$/;
  regOfCss = /\.css$/;
  regOfImg = /\.(png|jpg|jpeg|gif|ico)$/;
  // 不收集耗时的接口。
  regOfNoneInterface = /google-analytics/;
  // 收集耗时的接口。
  regOfInterface = new RegExp(`(${window.location.host})|(${process.env.REACT_APP_SASS_DOMAIN})`);
  // 需要收集的js文件
  regOfJSResource = /(main)|(polyfill\.min)/;
  // 提取url根路由
  regOfUrl = /\/[a-zA-z-]+(?=\/)/;
  gaCategory = "";
  ga4Category = "";
  /**
   * 需要统计的时间点信息。in***：表示时间段，at***：表示时间点
   */
  trackTimeList = {
    // onload事件触发时间点
    atOnload: [],
    // large-contentful-paint
    atLcp: [],
    // fitst-meaningful-paint
    atFmp: [],
    // 资源耗时
    inJSResource: [],
    inCSSResource: [],
    // 接口耗时
    inInterface: [],
    // 长任务耗时统计（只在测试环境进行收集）
    inLongTasks: []
  };
  // 需要上报的时间点信息。
  reportList = {};
  isDev = process.env.APP_NORMALIZED_ENV === "DEV";
  baseDom = document.body;
  perfAttr = "data-perf";

  init(baseGaCategory, baseGa4Category) {
    // GA用户计时category
    this.gaCategory = baseGaCategory;
    this.ga4Category = baseGa4Category;
    if (window.performance && window.PerformanceObserver && window.MutationObserver) {
      this.supportedEntryTypes = window.PerformanceObserver.supportedEntryTypes || [];
      this.ready = true;
      this.perf = window.performance;
      this.perfObserver = window.PerformanceObserver;
      this.pathName = window.location.pathname;
      this.initResources();
      this.initOnload();
      this.initLCP();
      this.isDev && this.initLongTask();
      this.initFMP();
      window.__perf__ = this.trackTimeList;
    } else {
      this.handleTrackFail();
      console.warn(`PerfTrack初始化失败!!!`);
    }
  }

  /**
   * 初始化PerformanceObserver
   */
  initResources = () => {
    if (!this.supportedEntryTypes.includes("resource")) return;
    this.resourceObserver = new this.perfObserver(entryList => {
      for (const entry of entryList.getEntries()) {
        this.trackResources(entry);
      }
    });
    this.resourceObserver.observe({ type: "resource", buffered: true });
  };

  /**
   * 收集load触发的时间
   */
  initOnload = () => {
    window.addEventListener("load", () => {
      this.trackTimeList.atOnload.push(this.fnDot());
    });
  };

  /**
   * 收集LCP时间点
   */
  initLCP = () => {
    if (!this.supportedEntryTypes.includes("largest-contentful-paint")) return;
    const observe = new this.perfObserver(entryList => {
      for (const entry of entryList.getEntries()) {
        this.trackLCP(entry);
        if (this.trackTimeList.atLcp.length > 0) {
          observe.disconnect();
        }
      }
    });
    observe.observe({ type: "largest-contentful-paint", buffered: true });
  };

  /**
   * 收集longTask耗时
   */
  initLongTask = () => {
    if (!this.supportedEntryTypes.includes("longtask")) return;
    this.longTaskObserver = new this.perfObserver(entryList => {
      for (const entry of entryList.getEntries()) {
        this.trackLongTasks(entry);
      }
    });
    this.longTaskObserver.observe({ type: "longtask", buffered: true });
  };

  initFMP = () => {
    this.fmpObserver = new MutationObserver(() => {
      this.handleUrlChange();
      this.fnHandleFMPDom(this.baseDom);
    });
    this.fmpObserver.observe(this.baseDom, {
      // 观察器的配置（需要观察什么变动）
      childList: true,
      subtree: true
    });
  };

  fnHandleFMPDom = dom => {
    if (!dom) return;
    const visible = this.fnJudgeDomVisible(dom);
    if (!visible) return;
    const dataExp = dom.getAttribute(this.perfAttr);
    if (dataExp) {
      this.trackFMP();
      return;
    }
    const children = dom.children;
    if (children.length > 0) {
      for (let child of children) {
        this.fnHandleFMPDom(child);
      }
    }
  };

  fnJudgeDomVisible = dom => {
    const { display, visibility, opacity } = getComputedStyle(dom) || {};
    // 判断元素是否不可见
    if (display === "none" || visibility === "hidden" || opacity === "0") return false;
    const { width, height } = dom.getBoundingClientRect();
    // 判断元素是否有宽高
    if (width <= 0 || height <= 0) return false;
    return true;
  };

  trackLongTasks = entry => {
    this.trackTimeList.inLongTasks.push(entry);
  };

  trackLCP = entry => {
    const { renderTime } = entry;
    this.trackTimeList.atLcp.push(renderTime);
  };

  /**
   * 根据initiatorType和文件名进行resource耗时收集。
   */
  trackResources = entry => {
    const { duration, name, initiatorType, startTime, responseEnd } = entry;
    switch (initiatorType) {
      case "script":
        if (this.regOfJSResource.test(name)) {
          this.trackTimeList.inJSResource.push({
            name,
            duration,
            startTime,
            responseEnd
          });
        }
        break;
      case "link":
        if (this.regOfCss.test(name)) {
          this.trackTimeList.inCSSResource.push({
            name,
            duration,
            startTime,
            responseEnd
          });
        } else {
          this.trackTimeList.inLinkResource.push({
            name,
            duration
          });
        }
        break;
      case "xmlhttprequest":
        this.fnMatchInterface(name) &&
          this.trackTimeList.inInterface.push({
            name,
            duration
          });
        break;
      default:
        if (this.regOfJs.test(name)) {
          this.trackTimeList.inJSResource.push({
            name,
            duration,
            startTime,
            responseEnd
          });
        }
        break;
    }
  };

  trackFMP = () => {
    const time = this.fnDot();
    this.trackTimeList.atFmp.push(time);
    // FMP收集以后进行数据上报。
    this.report();
  };

  fnDot = () => {
    return this.perf.now();
  };

  report = () => {
    // 当前页面url未改变，进行数据上报。
    if (!this.urlChanged) {
      for (let key in this.trackTimeList) {
        const item = this.trackTimeList[key];
        if (Array.isArray(item) && item.length > 0) {
          item.forEach(time => {
            const timingData = {
              category: this.gaCategory,
              variable: key
            };
            let originValue;
            if (typeof time === "number") {
              timingData.value = this.fnFormatTime(time);
              originValue = Math.floor(time);
              timingData.label = this.getFormatPathName(this.pathName);
            } else if (typeof time === "object") {
              const { name, duration } = time;
              originValue = Math.floor(duration);
              timingData.value = this.fnFormatTime(duration);
              timingData.label = name;
            }
            ReactGA.timing(timingData);
            // 上报到GA4
            // commonStore.gTag &&
            //   commonStore.gTag(
            //     "event",
            //     this.ga4Category,
            //     Object.assign(
            //       { ...timingData },
            //       { value: originValue, category: this.ga4Category }
            //     )
            //   );
            this.isDev && console.log(timingData);
          });
        }
      }
      this.handleTrackSuc();
    }
    this.afterReport();
  };

  /**
   * 返回去除参数的路由pathname
   * @param {*} pathName
   */
  getFormatPathName = pathName => {
    const res = pathName.match(this.regOfUrl);
    if (!res) return pathName;
    if (res && res[0]) return res[0];
  };

  /**
   * 返回取整的时间，乘以1000是因为GA支持的最小颗粒为s，分析需要用ms。
   * @param {*} time
   * @returns 处理过的耗时数值
   */
  fnFormatTime = time => Math.floor(time) * 1000;

  /**
   * 匹配进行上报的接口
   * @param {*} interfaceName 接口名
   * @returns 是否匹配成功
   */
  fnMatchInterface = interfaceName =>
    this.regOfInterface.test(interfaceName) && !this.regOfNoneInterface.test(interfaceName);

  /**
   * 上报以后需要清除定时和监听
   */
  afterReport = () => {
    this.resourceObserver && this.resourceObserver.disconnect();
    this.longTaskObserver && this.longTaskObserver.disconnect();
    this.fmpObserver && this.fmpObserver.disconnect();
  };

  /**
   * 监听页面url改变
   */
  handleUrlChange = e => {
    const newPathName = window.location.pathname;
    if (newPathName === this.pathName) return;
    this.urlChanged = true;
  };

  /**
   * 成功上报以后进行埋点。
   */
  handleTrackSuc = () => {
    ReactGA.event({
      category: "perfTrack",
      action: "success",
      label: this.gaCategory
    });
    // 上报到GA4
    // commonStore.gTag &&
    //   commonStore.gTag("event", this.ga4Category, {
    //     action: "success",
    //   });
  };

  /**
   * 上报失败进行统计
   */
  handleTrackFail = () => {
    ReactGA.event({
      category: "perfTrack",
      action: "fail",
      label: this.gaCategory
    });
    // 上报到GA4
    // commonStore.gTag &&
    //   commonStore.gTag("event", this.ga4Category, {
    //     action: "fail",
    //   });
  };
}

export default PerfTrack;



// WEBPACK FOOTER //
// ./src/lego/perfTrack.js